/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 7/7/21.
 */

import moment from "moment-timezone";
import * as _ from "lodash";
import {logData} from "@/plugins/CustomLog";
import store from "@/store";
import PlaylistApi from "@/services/Playlist";
import ErrorCode from "@/enums/ErrorCode";
import EventTypes from "@/enums/EventTypes";
import EventStatus from "@/enums/EventStatus";
import EventVerifyCode from "@/enums/EventVerifyCode";
import PlaylistTypes from "@/enums/PlaylistTypes";
import SequencerStatus from "@/enums/SequencerStatus";

const recalcDebouncers = {};

export default {
	state: {
		loadedPlaylists: {},
		loadedPlaylistsNoAssets: {},
		playlistViewSubscriptions: {},
		stationLogViewSubscriptions: {},
		loadedShowEpisodePlaylists: {},
		switchToAutoMode: {},
		blinkingEvent: {},
		auditionAudioIsOpen: {},
		emptyPlaylists: {},
		stationPlaylistsUsageCounterMap: {},
		assetPOIMarkupOverrides: {}
	},

	getters: {
		loadedPlaylistsByStationID: state => stationID => {
			return state.loadedPlaylists?.[stationID] ?? [];
		},

		loadedSchedulePlaylistsByStationID: state => stationID => {
			return (
				state.loadedPlaylists?.[stationID]?.filter(p =>
					[PlaylistTypes.Schedule, PlaylistTypes.SpecialShow].includes(p.PlaylistType)
				) ?? []
			);
		},

		playlistsNoAssetsLoadedByDateRange: state => (stationID, dateRange) => {
			return Object.prototype.hasOwnProperty.call(state.loadedPlaylistsNoAssets[stationID] ?? {}, dateRange);
		},

		loadedPlaylistsNoAssetsByStationID: state => stationID => {
			return state.loadedPlaylistsNoAssets[stationID] ?? {};
		},

		loadedPlaylistsNoAssetsByDate: state => (stationID, date) => {
			const from = date.clone().startOf("week").format("YYYY-MM-DD");
			const to = date.clone().endOf("week").format("YYYY-MM-DD");

			return state.loadedPlaylistsNoAssets[stationID]?.[`${from}_${to}`]?.filter(p =>
				date.isSame(p.ScheduledStartDateTime, "day")
			);
		},

		getPlaylistById:
			state =>
			({stationID, playlistID} = {}) => {
				const playlists = stationID
					? state.loadedPlaylists[stationID]
					: Object.values(state.loadedPlaylists).flat();

				return playlists?.find(p => p.PlaylistID === playlistID);
			},

		getPlaylistByDateTime: (state, getters) => (stationID, dateTime) => {
			return getters
				.loadedSchedulePlaylistsByStationID(stationID)
				.find(p => moment(p.ScheduledStartDateTime).isSame(dateTime));
		},

		playlistViewSubscriptions: state => (stationID, componentID) => {
			return state.playlistViewSubscriptions[stationID]?.[componentID] ?? [];
		},

		stationLogViewSubscriptions: state => (stationID, componentID) => {
			return state.stationLogViewSubscriptions[stationID]?.[componentID] ?? [];
		},

		isPlaylistSubscribed: state => (stationID, playlistID) => {
			const playlistSubscriptions = state.playlistViewSubscriptions[stationID] || {};
			const logSubscriptions = state.stationLogViewSubscriptions[stationID] || {};

			const isSubscribedToPlaylist = Object.values(playlistSubscriptions).some(subscriptions =>
				subscriptions.some(p => p.PlaylistID === playlistID)
			);
			const isSubscribedToLog = Object.values(logSubscriptions).some(subscriptions =>
				subscriptions.some(p => p.PlaylistID === playlistID)
			);

			return isSubscribedToPlaylist || isSubscribedToLog;
		},

		loadedShowEpisodePlaylists: state => (stationID, showEpisodeID) => {
			return state.loadedShowEpisodePlaylists[stationID]?.[showEpisodeID];
		},

		getBlockParentPlaylist:
			state =>
			({stationID, playlistID} = {}) => {
				return state.loadedPlaylists[stationID]?.find(p =>
					p.Events.some(e => e.ChildPlaylistID === playlistID)
				);
			},

		getEventByID:
			state =>
			({stationID, eventID}) => {
				let event;
				const playlists = state.loadedPlaylists[stationID] || [];
				for (const playlist of playlists) {
					event = playlist.Events?.find(e => e.EventID === eventID);
					if (event) {
						return event;
					}
				}
				return event;
			},

		getPlaylistEvents:
			state =>
			({stationID, playlistID}) => {
				const playlist = state.loadedPlaylists[stationID]?.find(p => p.PlaylistID === playlistID);
				return playlist?.Events ?? [];
			},

		getEventStatus:
			(state, getters) =>
			({event, stationID, eventID}) => {
				if (!event) {
					event = getters.getEventByID({stationID, eventID});
				}

				const playlist = getters.getPlaylistById({stationID: event.StationID, playlistID: event.PlaylistID});

				return {
					isCurrent: event?.StatusCode === EventStatus.Current,
					isPast:
						[EventStatus.Played, EventStatus.PendingPlayed, EventStatus.NotPlayed].includes(
							event?.StatusCode
						) ||
						(playlist?.HotlineSequence && playlist.HotlineSequence > event.SequenceNumber)
				};
			},

		isPlaylistEmpty:
			state =>
			({stationID, playlistID}) => {
				const playlist = state.loadedPlaylists[stationID]?.find(({PlaylistID}) => PlaylistID === playlistID);
				return !playlist?.Events || playlist?.Events?.length === 0;
			},

		getSwitchToAutoMode: state => stationID => {
			return state.switchToAutoMode[stationID] ?? true;
		},

		getBlinkingEvent: state => eventID => {
			return state.blinkingEvent[eventID];
		},

		auditionAudioIsOpen: state => eventID => {
			return state.auditionAudioIsOpen[eventID];
		},

		getEmptyPlaylists: state => stationID => {
			return state.emptyPlaylists[stationID] ?? [];
		},

		getAssetPOIMarkupOverride: state => assetID => {
			return state.assetPOIMarkupOverrides[assetID];
		}
	},

	mutations: {
		incrementStationPlaylistModuleCounter(state, stationId) {
			if (!state.stationPlaylistsUsageCounterMap[stationId]) {
				state.stationPlaylistsUsageCounterMap[stationId] = 0;
			}
			state.stationPlaylistsUsageCounterMap[stationId]++;
		},

		addLoadedPlaylist(state, playlist) {
			let stationPlaylists = state.loadedPlaylists[playlist.StationID] || [];

			const loadedPlaylist = stationPlaylists.some(p => p.PlaylistID === playlist.PlaylistID);
			if (!loadedPlaylist) {
				stationPlaylists = _.sortBy([...stationPlaylists, playlist], "ScheduledStartDateTime");
				state.loadedPlaylists[playlist.StationID] = stationPlaylists;
			}
		},

		removeLoadedPlaylist(state, {stationID, playlistID}) {
			const index = state.loadedPlaylists[stationID]?.findIndex(p => p.PlaylistID === playlistID);
			if (index > -1) {
				state.loadedPlaylists[stationID].splice(index, 1);
			}

			if (_.isEmpty(state.loadedPlaylists[stationID])) {
				delete state.loadedPlaylists[stationID];
			}
		},

		updatePlaylist(state, data) {
			const playlist = state.loadedPlaylists[data.stationID]?.find(p => p.PlaylistID === data.PlaylistID);

			if (playlist) {
				if (data.HotlineSequence !== undefined) {
					playlist.HotlineSequence = data.HotlineSequence;
				}

				if (data.TargetLength) {
					playlist.TargetLength = data.TargetLength;
				}
			}
		},

		addLoadedPlaylistNoAssets(state, {stationID, dateRange, playlists}) {
			if (!state.loadedPlaylistsNoAssets[stationID]) {
				state.loadedPlaylistsNoAssets[stationID] = {};
			}
			state.loadedPlaylistsNoAssets[stationID][dateRange] = playlists;
		},

		removeLoadedPlaylistNoAssets(state, {stationID, dateRange}) {
			delete state.loadedPlaylistsNoAssets[stationID][dateRange];

			if (_.isEmpty(state.loadedPlaylistsNoAssets[stationID])) {
				delete state.loadedPlaylistsNoAssets[stationID];
			}
		},

		playlistEventAdded(state, event) {
			const playlist = state.loadedPlaylists[event.StationID]?.find(p => p.PlaylistID === event.PlaylistID);
			if (playlist) {
				const isEventAlreadyPresent = playlist.Events?.find(e => e.EventID === event.EventID);

				if (!isEventAlreadyPresent) {
					playlist.Events.push(event);
					playlist.Events = _.sortBy(playlist.Events, "SequenceNumber");
				}
			}
		},

		playlistEventUpdated(state, event) {
			if (event.PlaylistID) {
				const playlist = Object.values(state.loadedPlaylists)
					.flat()
					?.find(p => p.PlaylistID === event.PlaylistID);

				// Update event in the playlist.
				const index = playlist?.Events?.findIndex(e => e.EventID === event.EventID) ?? -1;
				if (index > -1) {
					const sequencerItem = store.getters.getSequencerItemByEventID(playlist.StationID, event.EventID);
					if (sequencerItem?.StartDateTime && sequencerItem?.UsableTime) {
						event.AirStartDateTime = sequencerItem.StartDateTime;
					}

					_.merge(playlist.Events[index], event);
				}
			}
		},

		updateAssetPOIMarkupInEvents(state, {AssetID, POIMarkup}) {
			state.assetPOIMarkupOverrides = {...state.assetPOIMarkupOverrides, [AssetID]: POIMarkup};
		},

		playlistEventDeleted(state, {playlist, msg}) {
			const index = playlist?.Events?.findIndex(e => e.EventID === msg.EventID);
			playlist?.Events?.splice(index, 1);
		},

		playlistEventPositionChanged(state, {data, initialPlaylist, targetPlaylist}) {
			const index = targetPlaylist?.Events?.findIndex(({EventID}) => EventID === data.EventID);
			let updatedEvent = data;

			if (index > -1) {
				Object.keys(updatedEvent).forEach(key => {
					targetPlaylist.Events[index][key] = updatedEvent[key];
				});
			} else {
				// moved event to another playlist
				// Add event to the target playlist
				const initialEvent = initialPlaylist?.Events?.find(({EventID}) => EventID === data.EventID);
				updatedEvent = {...initialEvent, ...data};
				targetPlaylist.Events.push(updatedEvent);

				// Remove from the previous one
				const eventInitialIndex = initialPlaylist.Events.findIndex(({EventID}) => EventID === data.EventID);
				initialPlaylist.Events.splice(eventInitialIndex, 1);
			}
			// Sort target playlist by the SequenceNumber attribute
			targetPlaylist.Events = _.sortBy(targetPlaylist.Events, "SequenceNumber");
		},

		addPlaylistViewSubscription(state, {playlist, componentID}) {
			if (!state.playlistViewSubscriptions[playlist.StationID]) {
				state.playlistViewSubscriptions[playlist.StationID] = {};
			}
			let subscriptions = state.playlistViewSubscriptions[playlist.StationID][componentID] || [];

			const alreadySubscribed = subscriptions.some(p => p.PlaylistID === playlist.PlaylistID);
			if (!alreadySubscribed) {
				subscriptions = _.sortBy([...subscriptions, playlist], "ScheduledStartDateTime");

				state.playlistViewSubscriptions[playlist.StationID][componentID] = subscriptions;
			}
		},

		removePlaylistViewSubscription(state, {playlist, componentID}) {
			const index = state.playlistViewSubscriptions[playlist.StationID]?.[componentID]?.findIndex(
				p => p.PlaylistID === playlist.PlaylistID
			);

			if (index > -1) {
				state.playlistViewSubscriptions[playlist.StationID][componentID].splice(index, 1);
			}

			if (_.isEmpty(state.playlistViewSubscriptions[playlist.StationID]?.[componentID])) {
				delete state.playlistViewSubscriptions[playlist.StationID][componentID];
			}
		},

		addStationLogViewSubscription(state, {playlist, componentID}) {
			if (!state.stationLogViewSubscriptions[playlist.StationID]) {
				state.stationLogViewSubscriptions[playlist.StationID] = {};
			}
			let subscriptions = state.stationLogViewSubscriptions[playlist.StationID][componentID] || [];

			const alreadySubscribed = subscriptions.some(p => p.PlaylistID === playlist.PlaylistID);
			if (!alreadySubscribed) {
				subscriptions = _.sortBy([...subscriptions, playlist], "ScheduledStartDateTime");

				state.stationLogViewSubscriptions[playlist.StationID][componentID] = subscriptions;
			}
		},

		removeStationLogViewSubscription(state, {playlist, componentID}) {
			const index = state.stationLogViewSubscriptions[playlist.StationID]?.[componentID]?.findIndex(
				p => p.PlaylistID === playlist.PlaylistID
			);

			if (index > -1) {
				state.stationLogViewSubscriptions[playlist.StationID][componentID].splice(index, 1);
			}

			if (_.isEmpty(state.stationLogViewSubscriptions[playlist.StationID]?.[componentID])) {
				delete state.stationLogViewSubscriptions[playlist.StationID][componentID];
			}
		},

		setLoadedShowEpisodePlaylist: (state, {stationID, showEpisodeID, playlists}) => {
			if (!state.loadedShowEpisodePlaylists[stationID]) {
				state.loadedShowEpisodePlaylists[stationID] = {};
			}

			const timeFrame = _.map(playlists, p => moment(p.ScheduledStartDateTime));
			state.loadedShowEpisodePlaylists[stationID][showEpisodeID] = {
				playlists: _.map(playlists, "PlaylistID"),
				start: _.min(timeFrame),
				end: _.max(timeFrame)
			};
		},

		setSwitchToAutoMode(state, {stationID, switchToAutoMode}) {
			state.switchToAutoMode[stationID] = switchToAutoMode;
		},

		setBlinkingEvent(state, data) {
			if (data.status) {
				state.blinkingEvent[data.eventID] = data.status;
			} else {
				delete state.blinkingEvent[data.eventID];
			}
		},

		setAuditionAudioIsOpen(state, {eventID, status}) {
			if (status) {
				state.auditionAudioIsOpen[eventID] = status;
			} else {
				delete state.auditionAudioIsOpen[eventID];
			}
		},

		addEmptyPlaylist(state, playlist) {
			const stationEmptyPlaylists = state.emptyPlaylists[playlist.StationID] || [];
			if (!stationEmptyPlaylists.find(p => p.ScheduledStartDateTime === playlist.ScheduledStartDateTime)) {
				stationEmptyPlaylists.push(playlist);
			}
			state.emptyPlaylists[playlist.StationID] = stationEmptyPlaylists;
		},

		removeEmptyPlaylist(state, playlist) {
			const stationEmptyPlaylists = state.emptyPlaylists[playlist.StationID] || [];
			const index = stationEmptyPlaylists.findIndex(p => p.PlaylistID === playlist.PlaylistID);
			if (index > -1) {
				stationEmptyPlaylists.splice(index, 1);
			}
			state.emptyPlaylists[playlist.StationID] = stationEmptyPlaylists;
		},

		clearStationPlaylists(state, stationID) {
			if (state.stationPlaylistsUsageCounterMap[stationID] === 1) {
				delete state.loadedPlaylists[stationID];
				delete state.loadedShowEpisodePlaylists[stationID];
				delete state.loadedPlaylistsNoAssets[stationID];
				delete state.emptyPlaylists[stationID];
				delete state.playlistViewSubscriptions[stationID];
				delete state.stationLogViewSubscriptions[stationID];
			}
			state.stationPlaylistsUsageCounterMap[stationID]--;
		},

		resetPlaylistState(state) {
			state.loadedPlaylists = {};
			state.loadedPlaylistsNoAssets = {};
			state.playlistViewSubscriptions = {};
			state.stationLogViewSubscriptions = {};
			state.loadedShowEpisodePlaylists = {};
			state.switchToAutoMode = {};
			state.blinkingEvent = {};
			state.auditionAudioIsOpen = {};
			state.emptyPlaylists = {};
			state.stationPlaylistsUsageCounterMap = {};
		}
	},

	actions: {
		getPlaylistBySchedule(state, {stationID, from, to, params = {}}) {
			from = from.toISOString();
			to = to.toISOString();

			return PlaylistApi.getPlaylistBySchedule(stationID, from, to, params);
		},

		getPlaylistByShowEpisodeID(state, {stationID, showEpisodeID}) {
			return PlaylistApi.getPlaylistByShowEpisodeID(showEpisodeID).then(response =>
				response.data.find(p => p.StationID === stationID)
			);
		},

		getPlaylistWithAssetsByShowEpisodeID({dispatch}, {stationID, showEpisodeID, recalculate = true}) {
			return PlaylistApi.getPlaylistWithAssetsByShowEpisodeID(stationID, showEpisodeID).then(response => {
				let playlists = response.data.filter(p => {
					return (
						p.StationID === stationID && //Return the playlists for the selected Station only
						[PlaylistTypes.Schedule, PlaylistTypes.SpecialShow].includes(p.PlaylistType)
					); //We want to display only the Schedule and Special playlists
				});

				if (!playlists.length) {
					return [];
				}
				playlists.forEach(playlist => {
					dispatch("formatPlaylist", playlist);
				});
				if (recalculate) {
					dispatch("recalculateDebounce", {stationID, playlistsToRecalc: playlists});
				}
				return playlists;
			});
		},

		getPlaylistWithAssetsBySchedule(
			{getters, dispatch},
			{stationID, from, to, special = false, recalcLoadedPlaylists = true, formatAndRecalcPlaylists = true}
		) {
			let params = {};
			if (special) {
				params = {PlaylistType: 13};
			}
			from = from.clone().startOf("hour").toISOString();
			to = to.clone().endOf("hour").toISOString();
			return PlaylistApi.getPlaylistWithAssetsBySchedule(stationID, from, to, params)
				.then(async response => {
					let playlists = [];
					if (response.status === 200 && response.data[0]?.StationID === stationID) {
						let date = moment.utc(from);
						while (date.isSameOrBefore(to)) {
							let playlist = response.data.find(p => {
								return p.StationID === stationID && date.isSame(p.ScheduledStartDateTime);
							});

							if (playlist) {
								if (formatAndRecalcPlaylists) {
									dispatch("formatPlaylist", playlist);
								}
								playlists.push(playlist);
							}
							date.add(1, "hours");
						}
						if (formatAndRecalcPlaylists && playlists.length > 0) {
							const playlistsToRecalc = recalcLoadedPlaylists
								? getters.loadedSchedulePlaylistsByStationID(stationID)
								: playlists;
							dispatch("recalculateDebounce", {stationID, playlistsToRecalc});
						}
					}
					return playlists;
				})
				.catch(error => {
					console.log(error);
					return [];
				});
		},

		getPlaylistWithAssetsById({dispatch}, {playlist_id, recalculate = true}) {
			return PlaylistApi.getPlaylistWithAssets(playlist_id)
				.then(response => {
					let playlist = response.data;
					dispatch("formatPlaylist", playlist);
					if (recalculate) {
						dispatch("recalculatePlaylists", {playlists: [playlist]});
					}

					return playlist;
				})
				.catch(error => {
					console.log(error);
					return [];
				});
		},

		formatPlaylist({commit, dispatch}, playlist) {
			if (!playlist.Events) {
				playlist.Events = [];
			}
			playlist.Events.forEach(e => {
				if (!e.StatusCode) {
					e.StatusCode = EventStatus.Ready;
				}
				//todo: if there is not ChildPlaylist, then should I create it?
				if ([EventTypes.SpotBlock, EventTypes.LocalBreak].includes(e.EventType) && e.ChildPlaylist) {
					// Spotblock or Local Break
					dispatch("formatPlaylist", e.ChildPlaylist);
				}
			});
			commit("addLoadedPlaylist", playlist);
		},

		addRemovePlaylistSubscriptions({getters, commit, dispatch}, {playlists, componentID, subscribe = true}) {
			const promises = playlists.map(async playlist => {
				const stationID = playlist.StationID;
				const playlistID = playlist.PlaylistID;

				if (
					subscribe &&
					!getters.playlistViewSubscriptions(stationID, componentID).some(p => p.PlaylistID === playlistID)
				) {
					if (!getters.isPlaylistSubscribed(stationID, playlistID)) {
						await Promise.all([
							dispatch("playlistSubscription", {playlistID, subscribe}),
							dispatch("playlistEventSubscription", {playlistID, subscribe})
						]);
					}

					commit("addPlaylistViewSubscription", {playlist, componentID});
				} else if (
					!subscribe &&
					getters.playlistViewSubscriptions(stationID, componentID).some(p => p.PlaylistID === playlistID)
				) {
					commit("removePlaylistViewSubscription", {playlist, componentID});

					if (!getters.isPlaylistSubscribed(stationID, playlistID)) {
						await Promise.all([
							dispatch("playlistSubscription", {playlistID, subscribe}),
							dispatch("playlistEventSubscription", {playlistID, subscribe})
						]);

						commit("removeLoadedPlaylist", {stationID, playlistID});
					}
				}
			});
			return Promise.all(promises);
		},

		addRemoveStationLogSubscriptions({getters, commit, dispatch}, {playlists, componentID, subscribe = true}) {
			const promises = playlists.map(async playlist => {
				const stationID = playlist.StationID;
				const playlistID = playlist.PlaylistID;

				if (
					subscribe &&
					!getters.stationLogViewSubscriptions(stationID, componentID).some(p => p.PlaylistID === playlistID)
				) {
					if (!getters.isPlaylistSubscribed(stationID, playlistID)) {
						await Promise.all([
							dispatch("playlistSubscription", {playlistID, subscribe}),
							dispatch("playlistEventSubscription", {playlistID, subscribe})
						]);
					}

					commit("addStationLogViewSubscription", {playlist, componentID});
				} else if (
					!subscribe &&
					getters.stationLogViewSubscriptions(stationID, componentID).some(p => p.PlaylistID === playlistID)
				) {
					commit("removeStationLogViewSubscription", {playlist, componentID});

					if (!getters.isPlaylistSubscribed(stationID, playlistID)) {
						await Promise.all([
							dispatch("playlistSubscription", {playlistID, subscribe}),
							dispatch("playlistEventSubscription", {playlistID, subscribe})
						]);

						commit("removeLoadedPlaylist", {stationID, playlistID});
					}
				}
			});
			return Promise.all(promises);
		},

		updateSubscribedPlaylist({getters, commit, dispatch}, data) {
			if (data.operationType === "Modify") {
				commit("updatePlaylist", data);
			} else if (data.operationType === "Delete") {
				const playlist = getters.getPlaylistById({stationID: data.StationID, playlistID: data.PlaylistID});

				if (playlist) {
					dispatch("addRemovePlaylistSubscriptions", {playlists: [data], subscribe: false});
					dispatch("addRemoveStationLogSubscriptions", {playlists: [data], subscribe: false});

					commit("SHOW_SUCCESS_NOTIFICATION", {
						message: `The loaded playlist <b>${playlist.Title}</b> has been deleted.`
					});
				}
			}
		},

		recalculateDebounce({getters, commit, dispatch}, {stationID, playlistsToRecalc = []} = {}) {
			if (!recalcDebouncers[stationID]) {
				recalcDebouncers[stationID] = _.debounce(async (stationID, playlists) => {
					if (getters.getSequencerStatus(stationID) === SequencerStatus.Idle.val) {
						playlists = playlists.filter(p =>
							moment(p.ScheduledStartDateTime).isAfter(moment().add(1, "hour"))
						);
					}

					const playlistsResult = await dispatch("recalculatePlaylists", {
						stationID,
						playlists
					});

					playlistsResult.forEach(playlist => {
						commit("addLoadedPlaylist", playlist);
					});
				}, 1000);
			}

			// call the debounced function for this stationID
			recalcDebouncers[stationID](stationID, playlistsToRecalc);
		},

		async recalculatePlaylists({getters, commit}, {stationID, playlists}) {
			const playlistsWithEvents = playlists.reduce((array, p) => {
				const playlist = {...p};
				playlist.Events = p.Events.filter(e =>
					[EventStatus.Current, EventStatus.Ready, EventStatus.Paused].includes(e.StatusCode)
				);
				if (playlist.Events?.length > 0) {
					array.push(playlist);
				}
				return array;
			}, []);

			if (playlistsWithEvents.length) {
				await PlaylistApi.getAirTimes(playlistsWithEvents)
					.then(response => {
						const airTimes = response.data;
						logData("info", "RecalcPlaylist API Response", airTimes);

						if (airTimes.length === 0) {
							return;
						}

						const airTimesMap = new Map(airTimes.map(at => [at.EventID, at]));

						getters.loadedPlaylistsByStationID(stationID).forEach(playlist => {
							playlist?.Events?.forEach(event => {
								const eventAirTime = airTimesMap.get(event.EventID);

								if (eventAirTime) {
									if (
										event.EventType !== EventTypes.NoteEvent &&
										event.StatusCode !== EventStatus.NotPlayed &&
										!event.Skipped &&
										!eventAirTime.CalcStartTimeUtc.includes("0001-01-01", 0)
									) {
										event.AirStartDateTime = eventAirTime.CalcStartTimeUtc;
										event.ResyncType = eventAirTime.CalcForResync;

										// we need to check if an event falls within hour restrictions
										// for future events we should check the ErrorFlags property
										if (eventAirTime.ErrorFlags === EventVerifyCode.HourRestrictions) {
											event.ErrorCode = ErrorCode.HourRestrictions.val;
										}
									}
									if (event.EventType === EventTypes.ETM) {
										event.GapTime = eventAirTime.GapTime;
									}
								}
							});
						});
						return playlists;
					})
					.catch(error => {
						console.error("An error occurred when recalculating the playlist air times.", error);
						commit("SHOW_NOTIFICATION_WITH_TYPE", {
							message: "An error occurred when recalculating the playlist air times."
						});
					});
			}

			return playlists;
		},

		generateSequenceNumber(state, payload) {
			// in case of an empty placeholder event (empty spot block playlists) set Sequence Number as 1
			if (payload.eventID.includes("00000000-0000-0000-0000-000000000000")) {
				return {
					data: [1]
				};
			}
			return PlaylistApi.generateSequenceNumber(payload);
		},

		deletePlaylistEvent(state, eventID) {
			return PlaylistApi.deletePlaylistEvent(eventID);
		},

		updatePlaylistEvent(state, payload) {
			return PlaylistApi.updatePlaylistEvent(payload);
		},

		async playlistEventMessage({getters, commit, dispatch}, msg) {
			const isHotKeyEvent = getters.getPages().some(p => p.PlaylistID === msg.PlaylistID);
			let playlistsToRecalc = [];

			const operationType = msg.operationType;
			msg = _.omit(msg, ["coll", "db", "operationType", "removedFields", "time", "_id"]);
			if (msg.Asset) {
				msg.Asset = _.omit(msg.Asset, ["coll", "db", "operationType", "removedFields", "time"]);
			}

			if (operationType === "Add") {
				if (msg.EventType === EventTypes.PlayAsset && msg.AssetID && !msg.Asset) {
					msg.Asset = await dispatch("getAsset", msg.AssetID);
					msg.Asset.StationAssets =
						getters.getPlaylistById(msg.PlaylistID)?.Events.find(event => event.EventID === msg.EventID)
							?.Asset?.StationAssets ??
						(await dispatch("getStationAsset", {StationID: msg.StationID, AssetID: msg.AssetID}));
				} else if ([EventTypes.SpotBlock, EventTypes.LocalBreak].includes(msg.EventType)) {
					msg.ChildPlaylist = await dispatch("getPlaylistWithAssetsById", {
						playlist_id: msg.ChildPlaylistID,
						recalculate: false
					});
					dispatch("addRemovePlaylistSubscriptions", {playlists: [msg.ChildPlaylist]});
				}

				if (isHotKeyEvent) {
					// subscribe to Asset and StationAsset topics if not already have
					if (!getters.isSubscribedToAssetTopics(msg.AssetID)) {
						dispatch("assetSubscription", {assetID: msg.AssetID});
						dispatch("stationAssetSubscription", {assetID: msg.AssetID, stationID: msg.StationID});
					}
					commit("addAssetHotkeySubscription", {assetID: msg.AssetID, hotkey: msg});

					commit("addHotkey", _.omit(msg, ["removedFields"]));
					commit("setHotkeyLoading", {sequenceNumber: msg.SequenceNumber, loading: false});
				} else {
					commit("playlistEventAdded", msg);

					// Get the playlists that should be recalculated based on the event's playlist ScheduledStartDateTime
					let playlist = getters.getPlaylistById({stationID: msg.StationID, playlistID: msg.PlaylistID});

					if (
						[PlaylistTypes.SpotBlock, PlaylistTypes.NetworkSpotBlock, PlaylistTypes.DVTBlock].includes(
							playlist?.PlaylistType
						)
					) {
						playlist = getters.getBlockParentPlaylist({
							stationID: playlist.StationID,
							playlistID: msg.PlaylistID
						});
					}

					playlistsToRecalc = getters.loadedPlaylistsByStationID(playlist.StationID).filter(p => {
						return moment(p.ScheduledStartDateTime).isSameOrAfter(moment(playlist.ScheduledStartDateTime));
					});
				}
			} else if (operationType === "Modify") {
				if (isHotKeyEvent) {
					//In this case, there is a Hotkey Asset update, and we handle it from the Asset Notify topic
					if (msg.Asset) {
						return;
					}

					const pagePlaylist = getters.getPages().find(p => p.PlaylistID === msg.PlaylistID).HotKeys.data;
					const hotkey = pagePlaylist.Events.find(e => e.EventID === msg.EventID);

					// in the case of updating asset to the hotKey
					if (msg.AssetID && hotkey.AssetID !== msg.AssetID) {
						const stationID = pagePlaylist.StationID;
						msg.Asset = await dispatch("getAsset", msg.AssetID);

						// Unsubscribe from Asset and StationAsset topics if not other Event uses this Asset
						commit("removeAssetHotkeySubscription", {assetID: hotkey.AssetID, eventID: hotkey.EventID});
						if (!getters.isSubscribedToAssetTopics(hotkey.AssetID)) {
							dispatch("assetSubscription", {assetID: hotkey.AssetID, subscribe: false});
							dispatch("stationAssetSubscription", {
								assetID: hotkey.AssetID,
								stationID,
								subscribe: false
							});
						}

						// Subscribe to Asset and StationAsset topics if not already subscribed
						if (!getters.isSubscribedToAssetTopics(msg.AssetID)) {
							dispatch("assetSubscription", {assetID: msg.AssetID});
							dispatch("stationAssetSubscription", {assetID: msg.AssetID, stationID});
						}
						commit("addAssetHotkeySubscription", {assetID: msg.AssetID, hotkey});
					}

					dispatch("updateHotkeyDueToMessage", {hotkey, updatedData: msg});
				} else {
					let initialPlaylist;
					let targetPlaylist = getters.getPlaylistById({playlistID: msg.PlaylistID});
					const existingEvent = targetPlaylist?.Events?.find(e => e.EventID === msg.EventID);

					//in case of some of the event's attributes have changed or in case of drag & drop / copy & paste
					// in the same playlist
					if (existingEvent) {
						initialPlaylist = targetPlaylist;

						// in the case of event's asset added (e.g. voice track recorded or liner event resolved)
						if (
							msg.EventType === EventTypes.PlayAsset &&
							!msg.Asset &&
							msg.AssetID &&
							existingEvent.AssetID !== msg.AssetID
						) {
							msg.Asset = await dispatch("getAsset", msg.AssetID);
						}
					}
					// in case of cut & paste from another playlist
					else {
						if (targetPlaylist) {
							// if event found in one of the loaded playlists and spotBlock playlists,
							// then set the initial playlist variable
							initialPlaylist =
								getters.loadedPlaylistsByStationID(targetPlaylist.StationID).find(p => {
									return p.Events.some(e => e.EventID === msg.EventID);
								}) ?? msg.Playlist;
						}
					}

					// in case of drag & drop / copy & paste
					if (targetPlaylist && (!existingEvent || existingEvent.SequenceNumber !== msg.SequenceNumber)) {
						// update the event's position and data
						commit("playlistEventPositionChanged", {
							data: _.omit(msg, "Playlist"),
							initialPlaylist,
							targetPlaylist
						});
						if (
							[PlaylistTypes.SpotBlock, PlaylistTypes.NetworkSpotBlock, PlaylistTypes.DVTBlock].includes(
								targetPlaylist.PlaylistType
							)
						) {
							targetPlaylist = getters.getBlockParentPlaylist({
								stationID: targetPlaylist.StationID,
								playlistID: targetPlaylist.PlaylistID
							});
						}

						// From the playlists changed, get the one with the earlier ScheduledStartDateTime
						const playlistWithMinScheduledStartDateTime = _.minBy(
							[initialPlaylist, targetPlaylist],
							"ScheduledStartDateTime"
						);

						// Get the playlists that should be recalculated based on the event's playlist ScheduledStartDateTime
						playlistsToRecalc = getters.loadedPlaylistsByStationID(targetPlaylist.StationID).filter(p => {
							return moment(p.ScheduledStartDateTime).isSameOrAfter(
								moment(playlistWithMinScheduledStartDateTime.ScheduledStartDateTime)
							);
						});
					} else {
						if (initialPlaylist) {
							// update the event's data
							commit("playlistEventUpdated", msg);
							playlistsToRecalc = getters
								.loadedPlaylistsByStationID(targetPlaylist.StationID)
								.filter(p => {
									return moment(p.ScheduledStartDateTime).isSameOrAfter(
										moment(initialPlaylist.ScheduledStartDateTime)
									);
								});
						}
					}
				}
			} else if (operationType === "Delete") {
				const playlist = !isHotKeyEvent
					? getters.getPlaylistById({playlistID: msg.PlaylistID})
					: getters.getPageById(msg.PlaylistID).HotKeys.data;
				const deletedEvent = playlist?.Events?.find(e => e.EventID === msg.EventID);

				if (msg.EventType === EventTypes.SpotBlock && msg.ChildPlaylist) {
					//unsubscribe from spot block's playlist and playlistEvent topics
					dispatch("addRemovePlaylistSubscriptions", {playlists: [msg.ChildPlaylist], subscribe: false});
					dispatch("addRemoveStationLogSubscriptions", {playlists: [msg.ChildPlaylist], subscribe: false});
				} else if (msg.EventType === EventTypes.LocalBreak && msg.ChildPlaylist) {
					msg.ChildPlaylist.Events.forEach(e => {
						if (e.EventType === EventTypes.SpotBlock && e.ChildPlaylist) {
							//unsubscribe from child spot block event's playlist and playlistEvent topics
							dispatch("addRemovePlaylistSubscriptions", {
								playlists: [e.ChildPlaylist],
								subscribe: false
							});
							dispatch("addRemoveStationLogSubscriptions", {
								playlists: [e.ChildPlaylist],
								subscribe: false
							});
						}
					});
					//unsubscribe from the local break's playlist and playlistEvent topics
					dispatch("addRemovePlaylistSubscriptions", {playlists: [msg.ChildPlaylist], subscribe: false});
					dispatch("addRemoveStationLogSubscriptions", {playlists: [msg.ChildPlaylist], subscribe: false});
				}

				if (isHotKeyEvent) {
					// Unsubscribe from Asset and StationAsset topics if there are subscriptions
					if (getters.isSubscribedToAssetTopics(msg.AssetID)) {
						dispatch("assetSubscription", {assetID: msg.AssetID, subscribe: false});
						dispatch("stationAssetSubscription", {
							assetID: msg.AssetID,
							stationID: deletedEvent.StationID,
							subscribe: false
						});
						commit("removeAssetHotkeySubscription", {assetID: msg.AssetID, eventID: deletedEvent.EventID});
					}

					commit("removeHotkeyByEventId", msg.EventID);
				} else {
					if (deletedEvent) {
						commit("playlistEventDeleted", {playlist, msg});

						// if the deleted event is a future event, we need to recalculate the airtimes
						if (deletedEvent.SequenceNumber > playlist.HotlineSequence) {
							// Get the playlists that should be recalculated based on the event's playlist's ScheduledStartDateTime
							playlistsToRecalc = getters.loadedPlaylistsByStationID(playlist.StationID).filter(p => {
								return moment(p.ScheduledStartDateTime).isSameOrAfter(
									moment(playlist.ScheduledStartDateTime)
								);
							});
						}
					}
				}
			}

			if (playlistsToRecalc.length > 0) {
				dispatch("recalculateDebounce", {stationID: playlistsToRecalc[0].StationID, playlistsToRecalc});
			}
		},

		createPlaylist({dispatch}, payload) {
			return PlaylistApi.createPlaylist(payload).then(response => {
				dispatch("formatPlaylist", response.data);
				return response.data;
			});
		},

		createPlaylistEvent(state, payload) {
			return PlaylistApi.createPlaylistEvent(payload);
		},

		updatePlaylist(state, payload) {
			return PlaylistApi.updatePlaylist(payload);
		},

		getDefaultAssetTypes() {
			return PlaylistApi.getDefaultAssetTypes();
		},

		resolveTag(state, params) {
			return PlaylistApi.resolveTag(params);
		}
	}
};
